import { useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup'
import SelectMenu from '../../../../components/form-control/SelectMenu';
import DatePickerInput from '../../../../components/form-control/DatePickerInput';
import TextArea from '../../../../components/form/TextArea';
import Input from '../../../../components/form-control/Input';
import Button from '../../../../components/form-control/Button';
import { createExpense } from '../../../../api/requests';
import { INPUT_DATE_FORMAT } from '../../../../resources/constants';
import moment from 'moment';
import { showToast } from '../../../../utils/miscFunctions';

interface ICreateExpenseProp {
    onProceed: () => void
    onClose: () => void
}

interface ICreateExpenseDTO {
    title: string
    description: string
    date: string
    currency: string
    amount: string
}


const CreateExpense = ({ onProceed, onClose }: ICreateExpenseProp) => {
    const [submitting, setSubmitting] = useState(false);

    return (
        <Formik
            initialValues={{
                title: '',
                description: '',
                category: '',
                amount: '',
                currency: '',
                date: null,

            }}
            validationSchema={Yup.object().shape({
                amount: Yup.string().required('Transaction amount is required'),
                date: Yup.date().required('Date of transaction is required.').typeError('Select date of transaction'),
                description: Yup.string().required('Expense description is required'),
                category: Yup.string().required('Expense category is required'),
                currency: Yup.string().required('Transaction currency is required'),
                title: Yup.string().required('Expense title is required'),

            })}
            onSubmit={(values, { resetForm }) => {

                const payload: ICreateExpenseDTO = {
                    ...values,
                    title: values.title,
                    description: values.description,
                    currency: values.currency,
                    amount: values.amount,
                    date: values.date ? moment(values.date).format(INPUT_DATE_FORMAT) : ''
                };

                setSubmitting(true);

                createExpense(payload)
                    .then((response: any) => {
                        showToast.success(response.message ?? 'Expense created successfully');
                        onProceed();
                    })
                    .catch((error: any) => {
                        showToast.error(error.description);
                    })
                    .finally(() => {
                        setSubmitting(false);
                    });
            }}>
            {({ values, errors, touched, handleSubmit, handleChange, setFieldValue, handleBlur }) => (
                <form className="p-5" onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-center">
                        <Input
                            type="text"
                            id="title"
                            name="title"
                            onChange={handleChange}
                            onBlur={handleChange}
                            value={values.title}
                            label="Title"
                            placeholder="Expense title"
                            required
                            error={touched.title ? errors.title : undefined}
                            helperText={touched.title ? errors.title : undefined}
                        />

                        <SelectMenu
                            onChange={(option: string | null) => setFieldValue('category', option)}
                            onBlur={handleBlur}
                            listSelectedValue={values.category}
                            list={["Customer Acquisition Cost", "Office Expense", "Others"]}
                            placeholder='Select category'
                            name="category"
                            label="Category"
                            required
                            error={touched.category ? errors.category : undefined}
                            helperText={touched.category ? errors.category : undefined}
                        />

                        <SelectMenu
                            onChange={(option: string | null) => setFieldValue('currency', option)}
                            onBlur={handleBlur}
                            listSelectedValue={values.currency}
                            placeholder='Select currency'
                            name="currency"
                            label="Currency"
                            list={["GHC", "NGN", "USD"]}
                            required
                            error={touched.currency ? errors.currency : undefined}
                            helperText={touched.currency ? errors.currency : undefined}
                        />

                        <Input
                            type="number"
                            name="amount"
                            id="amount"
                            onChange={handleChange}
                            onBlur={handleChange}
                            value={values.amount}
                            label="Amount "
                            placeholder="Amount"
                            required
                            error={touched.amount ? errors.amount : undefined}
                            helperText={touched.amount ? errors.amount : undefined}
                        />

                        <DatePickerInput
                            id="date"
                            selected={values.date}
                            onChange={(date) => setFieldValue('date', date)}
                            classes='w-full'
                            dateFormat="MM-dd-yyyy"
                            label="Date of Transaction"
                            showYearDropdown
                            allowEarlierSelections={false}
                            error={touched.date ? errors.date : undefined}
                            helperText={touched.date ? errors.date : undefined}
                            required
                        />
                    </div>

                    <div className="mt-5">
                        <TextArea
                            onValueChanged={handleChange}
                            name="description"
                            id="description"
                            value={values.description}
                            placeholder="What's the expense about?"
                            label="Description"
                            onFocusOut={() => { }}
                        />
                    </div>

                    <div className="w-full flex gap-4 mt-5">
                        <Button
                        label="Cancel"
                        bgColor='secondary'
                  fullWidth
                        onClick={onClose}
                        />
                        <Button
                        label="Save"
                        onClick={handleSubmit}
                        bgColor='primary'
              fullWidth
                        loading={submitting}
                        disabled={submitting}
                        />
                    </div>
                </form>
            )}
        </Formik >
    );
};

export default CreateExpense;
